// @flow
import React, { useCallback } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { createStructuredSelector } from "reselect";
import cx from "classnames";
import t from "common/i18n";
import SvgIcon from "root/components/SvgIcon";
import getMediaQuery from "utils/sassVarsUtils";
import bindActions, { types as ats } from "../../actions";
import {
	selectIsAdvancedOptionsOpen,
	selectShouldShowAdvancedOptions,
	selectConfigLanguage,
	selectShowPoweredBy,
	selectDictionaryTextVersion,
} from "../../selectors";
import Footer from "../Footer";
import AdvancedOptions from "../AdvancedOptions";
import SelectedCounter from "../SelectedCounter";
import Button from "../Button";
import SVG_ICONS from "~/assets/icons/icons";

import "./TabFooter.scss";

type OwnProps = {|
    showSelection?: boolean,
    selectionCount?: number,
    clearSelectionHandler?: ?Function,
    uploadButtonHandler?: ?Function,
    children?: any,
    actionButtonText?: ?string,
    actionButtonIcon?: string,
    showActionButton?: boolean,
    actionButtonActive?: boolean,
    actionButton?: ?Function,
    showSecondaryButton?: boolean,
    secondaryButtonText?: ?string,
    secondaryButtonHandler?: ?Function,
    secondaryButton?: ?Function,
	hideAdvanced?: boolean,
|};

type Props = {
    ...OwnProps,
    shouldShowAdvanced: boolean,
    advancedOptionsOpen: boolean,
    showPoweredBy: boolean,
    language: string,
    textVersion: number,
    [string]: Function
};

const AdvancedBox = styled.div`
	${getMediaQuery("ns")} {
		left: ${({ showPoweredBy }) => showPoweredBy ? "72" : "20"}px;
	}
`;

export const TabFooter = (props: Props) => {
	const selectionOn = (props.showSelection && props.selectionCount) || props.actionButtonActive;
	const advancedOpen = props.advancedOptionsOpen;
	const dispatchToggleAdvancedOptions = props[ats.TOGGLE_ADVANCED_OPTIONS];

	const renderSelectionInfo = () => (selectionOn && props.selectionCount ?
		<SelectedCounter selectedCount={props.selectionCount}
		                 onClearClick={props.clearSelectionHandler}
		                 key="panel_search_select_counter"/> : null);

	const renderSecondaryButton = () => (props.secondaryButton ?
		props.secondaryButton :
		(props.showSecondaryButton && props.secondaryButtonText ?
			<Button className="secondary-button"
			        colorName="link"
			        onClick={props.secondaryButtonHandler}
			        text={props.secondaryButtonText}
			        key="footer_second_button"/> : null));

	const renderActionButton = () => (props.actionButton ?
		props.actionButton :
		(props.showSelection || props.showActionButton ? <Button className="action-button"
			colorName="action"
			dataTest="upload-btn"
			disabled={!selectionOn || props.actionButtonActive === false}
			icon={props.actionButtonIcon}
			text={props.actionButtonText || t("actions.upload")}
			onClick={props.uploadButtonHandler}
			key="footer_upload_button"/> : null));

	const toggleAdvancedOptions = useCallback(() => {
		dispatchToggleAdvancedOptions();
	}, [dispatchToggleAdvancedOptions]);

	const renderOpenAdvancedOptions = () => (advancedOpen ? <AdvancedOptions/> : null);

	const renderAdvancedBox = () => {
		const boxClasses = cx("advance-box flex flex-column bg-window ba",
			{
				active: advancedOpen,
				"bc-window-border": advancedOpen,
				"bc-window": !advancedOpen,
			},
		);

		return props.shouldShowAdvanced ? <AdvancedBox
			className={boxClasses}
			data-test="advanced-box"
			showPoweredBy={props.showPoweredBy}>

			<div className="flex items-center pointer pl3 pl0-ns btn-advance-wrap"
			     data-test="btn-advanced"
				 onClick={toggleAdvancedOptions}>

				<SvgIcon size="xxs" colorName="link"
				         path={SVG_ICONS[advancedOpen ? "KEYBOARD_DOWN" : "KEYBOARD_UP"]}/>

				<button data-test="btn-advanced"
						type="button"
				        className="btn-advance c-link ml2"
						tabIndex={0}>
					{t("advanced")}
				</button>
			</div>
			<div>
				{renderOpenAdvancedOptions()}
			</div>
		</AdvancedBox> : null;
	};

	return <Footer
		footerClassName={cx(`bt bc-window-border ${selectionOn ? "items-active" : ""}`)}>

		{!props.hideAdvanced && renderAdvancedBox()}

		<div className="flex items-center justify-end">
			{props.children}
			{renderSelectionInfo()}
			{renderSecondaryButton()}
			{renderActionButton()}
		</div>
	</Footer>;
};

export default connect<Props, OwnProps, _, _, _, _>(
	createStructuredSelector({
		shouldShowAdvanced: selectShouldShowAdvancedOptions,
		advancedOptionsOpen: selectIsAdvancedOptionsOpen,
		language: selectConfigLanguage,
		showPoweredBy: selectShowPoweredBy,
		textVersion: selectDictionaryTextVersion,
	}),
	bindActions,
)(TabFooter);
